import {DeliveryOption} from '@/product/delivery-option';
import {ProductOption} from '@/product/types';

/**
 * A strategy for determining the name of the option for a given step.
 * Also determines whether the variant options should be shown for a given step.
 * This allows us to support both delivery-first and variant-first flows.
 * Intended to be used by the [ProductSelectControllerSignal] class.
 */
export abstract class StepStrategy {
  productOptions: ProductOption[];
  deliveryOptions: DeliveryOption[];
  ignoreOptions: string[];

  constructor({deliveryOptions, productOptions}: StepStrategyOptions) {
    this.deliveryOptions = deliveryOptions;
    this.productOptions = productOptions;
  }

  /**
   * Returns the name of the option for the given step.
   */
  abstract getOptionName(step: number): string;

  /**
   * Returns true if the variant options should be shown for the given step.
   */
  abstract shouldShowVariantOptions(step: number): boolean;

  /**
   * Returns true if the variant image should be shown for the given step.
   * Otherwise, the featured product image should most likely be shown.
   */
  abstract shouldShowVariantImage(step: number): boolean;

  /**
   * Returns a new StepStrategy instance based on the given options.
   */
  static fromOptions(options: StepStrategyOptions): StepStrategy {
    return options.deliveryFirst
      ? new StepStrategyDeliveryFirst(options)
      : new StepStrategyVariantFirst(options);
  }
}

/**
 * A [StepStrategy] that assumes the first step is for variant options (ie, Choose Your Scent).
 */
class StepStrategyVariantFirst extends StepStrategy {
  getOptionName(step: number) {
    if (step > this.productOptions.length) {
      return this.productOptions[this.productOptions.length - 1].name;
    }
    return this.productOptions[step - 1].name;
  }
  shouldShowVariantOptions(step: number) {
    return step <= this.productOptions.length;
  }
  shouldShowVariantImage() {
    return true;
  }
}

/**
 * A [StepStrategy] that assumes the first step is for delivery options (ie, Choose Your Delivery).
 */
class StepStrategyDeliveryFirst extends StepStrategy {
  getOptionName(step: number) {
    const stepAdjusted = this.deliveryOptions.length === 0 ? step : step - 1;
    const noOption =
      stepAdjusted < 1 || stepAdjusted > this.productOptions.length;
    if (noOption) {
      return this.productOptions[0].name;
    }
    return this.productOptions[stepAdjusted - 1].name;
  }
  shouldShowVariantOptions(step: number) {
    return this.deliveryOptions.length === 0 || step > 1;
  }
  shouldShowVariantImage(step: number) {
    return this.shouldShowVariantOptions(step);
  }
}

export type StepStrategyOptions = {
  deliveryFirst: boolean;
  deliveryOptions: DeliveryOption[];
  productOptions: ProductOption[];
};
